import React from 'react';
import TenantSettingsPage from './TenantSettingsPage';
import SettingsAccessPage from './SettingsAccessPage';
import FinancialsSettingsHub from './FinancialsSettingsHub';
import FinancialsInputHub from './FinancialsInputHub';
import HrSocialHub from './hr/HrSocialHub';
import PlanVersionsPage from './PlanVersionsPage';
import DataExchangePage from './DataExchangePage';
import { SETTINGS_SECTION_IDS } from '../nav/settingsSections';
import { canExportData } from '../nav/entitlements';
import './SettingsHubPage.css';

/* Paramètres — hub affiché comme les autres modules : ses sections (Général,
   Utilisateur) vivent dans le panneau contextuel de la Sidebar (prop
   `activeSection`, pilotée par App). Cette page ne rend que le contenu.
     • general → devise, première année, nom
     • access  → utilisateur : mon compte, mon rôle, la grille des rôles (lecture)
   Réservé aux rôles de SETTINGS_ROLES (cf. nav/entitlements.js). */
export default function SettingsHubPage({
  activeTenant, onTenantPatched, onRenameTenant, onDeleteTenant, addToast, activeSection,
  authUser, roles, scopes, visibleModules, setPage, year, onYearChange,
}) {
  /* LES TAUX DE CHARGES SOCIALES : ADMINISTRATEUR DU CLIENT ET EXPLOITANT
     SEULEMENT (demande d'Olivier, 2026-09-28), quels que soient les modules.
     Le serveur refuse l'écriture aux autres rôles ; la section ne s'ouvre pas
     pour eux. */
  const isTenantAdmin = Boolean(authUser?.is_platform_admin) || (roles || []).includes('admin');
  /* LES DONNÉES : qui lit People ET Finance (2026-10-11), la même règle que le
     serveur. */
  const canExport = canExportData(roles, scopes, Boolean(authUser?.is_platform_admin));
  const wanted = SETTINGS_SECTION_IDS.includes(activeSection) ? activeSection : 'general';
  const active = (wanted === 'social' && !isTenantAdmin) || (wanted === 'data' && !canExport)
    ? 'general' : wanted;

  const tenantProps = {
    activeTenant, onTenantPatched, onRenameTenant, onDeleteTenant, addToast,
    embedded: true,
  };

  const renderSection = (id) => {
    switch (id) {
      // Le renommage n'est plus côté client : nom et slug relèvent de
      // l'Administration (identité contractuelle, décision 2026-08-20).
      case 'general': return <TenantSettingsPage {...tenantProps} sections={['company']} />;
      case 'access':  return <SettingsAccessPage authUser={authUser} roles={roles} scopes={scopes} activeTenant={activeTenant} visibleModules={visibleModules} />;
      // Les MÊMES écrans qu'avant, à leur nouvelle adresse : on déplace le
      // rangement, jamais le contenu.
      case 'finance':   return <FinancialsSettingsHub embedded activeTenant={activeTenant} onTenantPatched={onTenantPatched} addToast={addToast} year={year} />;
      case 'structure': return <FinancialsInputHub embedded activeTenant={activeTenant} setPage={setPage} addToast={addToast} year={year} onYearChange={onYearChange} />;
      /* MÊME ÉCRAN, DEUX ADRESSES (2026-09-27) : les taux vivent ici, au
         client, parce que Finance et le plan de liquidité les lisent ; People
         en garde une copie pour qu'on les règle sans quitter le module. C'est
         la MÊME table, jamais un doublon de données. La LPP, elle, ne suit
         pas : elle est nominative. */
      case 'social':    return <HrSocialHub embedded activeTenant={activeTenant} setPage={setPage} addToast={addToast} year={year} onYearChange={onYearChange} onTenantUpdated={onTenantPatched} only={['synthese', 'base', 'supp', 'phasing']} />;
      /* PLANIFICATION (2026-09-29) : l'horizon et le niveau de planification,
         venus de Général, puis les versions de plan. */
      case 'planning':  return (
        <>
          <TenantSettingsPage {...tenantProps} sections={['planning']} />
          <PlanVersionsPage activeTenant={activeTenant} addToast={addToast} roles={roles} authUser={authUser} />
        </>
      );
      /* DONNÉES (2026-10-11) : l'export Excel et la sauvegarde complète,
         rien d'autre ; l'import, la restauration, le renommage et la
         suppression restent à l'Administration. */
      case 'data':      return (
        <>
          <DataExchangePage activeTenant={activeTenant} addToast={addToast} exportOnly />
          <TenantSettingsPage {...tenantProps} sections={['backup']} />
        </>
      );
      default: return null;
    }
  };

  /* Le RETOUR vit en tête du PANNEAU du menu (2026-09-18), avec celui des trois
     adresses de l'exploitant : un seul endroit pour sortir d'un lieu qui n'est
     pas un module, et il vaut pour toutes ses pages. */
  return (
    <main className="page-shell page-table-layout is-fluid settings-page">
      <div className="settings-page__content">
        {renderSection(active)}
      </div>
    </main>
  );
}
